import React from "react";
import MatchRow, { PointsPill, shortTeamName } from "./ui/MatchRow";
import StatusPill from "./StatusPill";
import { oneMoreGoal } from "../utils/scorePoints";
import { isFixturePredictionOpen } from "../utils/fixtureDeadline";

function hasScore(match) {
  return match.home_score != null && match.away_score != null;
}

/**
 * Matches that are on right now: the score, your pick, points so far, and one more goal.
 */
export default function LiveNow({ matches, pickFor, logoFor, metaFor, updated, tableText }) {
  if (!matches?.length) return null;

  const swings = matches.map((match) => {
    const pick = pickFor(match);
    if (!pick || !hasScore(match)) return null;
    return oneMoreGoal(
      pick.predicted_home_score,
      pick.predicted_away_score,
      match.home_score,
      match.away_score
    );
  });
  const holding = swings.reduce((sum, swing) => sum + (swing?.now || 0), 0);
  const picked = swings.some(Boolean);
  const summary = picked
    ? `${holding} ${holding === 1 ? "pt" : "pts"} if the scores hold`
    : "No picks in these matches";

  return (
    <section aria-label="Live now">
      <div className="mb-2 flex items-end justify-between gap-3 px-1">
        <div className="min-w-0">
          <h2 className="text-[15px] font-semibold text-ink">Live now</h2>
          <p className="text-meta text-muted">
            {summary}
            {tableText ? ` · ${tableText}` : ""}
          </p>
        </div>
        {updated && <p className="shrink-0 text-[12px] text-muted">{updated}</p>}
      </div>
      <div className="space-y-2">
        {matches.map((match, index) => {
          const pick = pickFor(match);
          const scored = hasScore(match);
          const swing = swings[index];
          const live = String(match.status || "").toLowerCase() === "live";
          const pickLabel =
            pick && pick.predicted_home_score != null && pick.predicted_away_score != null
              ? `${pick.predicted_home_score}–${pick.predicted_away_score}`
              : null;
          return (
            <MatchRow
              key={match.id}
              competition={match.competition}
              live={live || scored}
              tone={live || scored ? "live" : undefined}
              to={
                isFixturePredictionOpen(match)
                  ? `/predictions?competition=${match.competition}`
                  : undefined
              }
              home={{ name: match.home_team, logo: logoFor(match, "home") }}
              away={{ name: match.away_team, logo: logoFor(match, "away") }}
              meta={metaFor ? metaFor(match) : null}
              metaRight={
                <StatusPill
                  status={match.status}
                  postponed={match.postponed}
                  live={live}
                  matchDate={match.match_date}
                />
              }
              center={
                <div className="text-center">
                  <p className="text-score font-bold tabular-nums text-ink">
                    {scored ? `${match.home_score}–${match.away_score}` : "–"}
                  </p>
                  <p className="text-[11px] text-muted tabular-nums">
                    {pickLabel ? `you ${pickLabel}` : scored ? "No pick" : "Score coming"}
                  </p>
                  {swing && (
                    <div className="mt-1">
                      <PointsPill points={swing.now} />
                    </div>
                  )}
                </div>
              }
              footer={
                swing && (
                  <p className="text-center text-[12px] text-muted tabular-nums">
                    {shortTeamName(match.home_team)} goal {swing.homeGoal}
                    <span> · {shortTeamName(match.away_team)} goal {swing.awayGoal}</span>
                  </p>
                )
              }
            />
          );
        })}
      </div>
    </section>
  );
}
